"use client"

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker"
import dayjs, { type Dayjs } from "dayjs"
import "dayjs/locale/he"
import {
  Controller,
  type Control,
  type FieldValues,
  type Path,
} from "react-hook-form"
import { colors } from "../../../theme/colors"
import ArrowDown from "../../../icons/ArrowDown"
import { pillFieldSx } from "./FormTextField"

/* Today = orange outline, selected day = orange gradient circle (EGDE-449).
   Day ordering (Sun → Sat, right-to-left in Hebrew) follows the MUI theme
   direction set by the app's ThemeProvider. */
const daySx = {
  width: 30,
  height: 30,
  fontSize: 12,
  "&.MuiPickersDay-today": {
    border: `1px solid ${colors.primitives.primary500}`,
    backgroundColor: "transparent",
  },
  "&.Mui-selected": {
    background: colors.gradients.gradientCta,
    color: colors.primitives.neutral0,
    "&:hover, &:focus": {
      background: colors.gradients.gradientCta,
    },
  },
  /* EGDE-449: the design's calendar shows the adjacent months' days greyed. */
  "&.MuiPickersDay-dayOutsideMonth": {
    color: colors.primitives.neutral300,
  },
} as const

/* EGDE-449: compact white rounded calendar card with a soft shadow, per
   Figma. The theme paints Paper surfaces with the grey `background.paper`
   token, so the white surface must be explicit here. */
const calendarPaperSx = {
  borderRadius: "16px",
  boxShadow: "0px 12px 32px rgba(0, 0, 0, 0.12)",
  backgroundColor: colors.primitives.neutral0,
  /* MUI gives DateCalendar a fixed 336px height, leaving a dead white band
     under short months — let it hug its rows instead. */
  "& .MuiDateCalendar-root": {
    width: 280,
    height: "auto",
    maxHeight: 316,
    paddingBottom: "8px",
  },
  "& .MuiPickersCalendarHeader-root": {
    minHeight: 36,
    marginTop: "12px",
    paddingInlineStart: "16px",
    paddingInlineEnd: "8px",
  },
  "& .MuiPickersCalendarHeader-label": {
    fontSize: 14,
    fontWeight: 500,
  },
  "& .MuiPickersCalendarHeader-switchViewButton, & .MuiPickersArrowSwitcher-button":
    {
      padding: "2px",
      "& svg": { fontSize: 18 },
    },
  "& .MuiDayCalendar-weekDayLabel": {
    width: 30,
    height: 28,
    fontSize: 11,
    color: colors.primitives.neutral300,
  },
  /* MUI reserves six week-rows (minHeight 240) — hug the month's actual
     weeks like the design; five 30px rows, six-week months grow by one. */
  "& .MuiDayCalendar-slideTransition": {
    minHeight: 170,
  },
} as const

interface FormDatePickerProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  label: string
  locale: string
  required?: boolean
  error?: string
}

export default function FormDatePicker<T extends FieldValues>({
  control,
  name,
  label,
  locale,
  required,
  error,
}: FormDatePickerProps<T>) {
  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={locale === "en" ? "en" : "he"}
    >
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          /* EGDE-449: the responsive DatePicker swaps to a full-screen mobile
             dialog on touch devices — the design shows the same dropdown
             calendar card everywhere (mirrors the DesktopTimePicker call in
             FormTimePicker, EGDE-259). */
          <DesktopDatePicker
            label={label}
            value={field.value ? dayjs(field.value as Date) : null}
            onChange={(value: Dayjs | null) =>
              // EGDE-257: MUI X emits a *Dayjs* (not null) even for incomplete or
              // out-of-range entries. Calling `.toDate()` on that produces an
              // `Invalid Date`, which yup.date() then reports as a typeError —
              // leaving a valid-looking field stuck in the red required state.
              // Mirror the time picker: only commit a real, valid date; otherwise
              // clear to null so `required` governs genuinely-empty fields.
              field.onChange(value && value.isValid() ? value.toDate() : null)
            }
            format="DD/MM/YYYY"
            disableFuture
            showDaysOutsideCurrentMonth
            slots={{ openPickerIcon: ArrowDown }}
            slotProps={{
              day: { sx: daySx },
              desktopPaper: { sx: calendarPaperSx },
              openPickerIcon: { width: 16, height: 8 },
              textField: {
                fullWidth: true,
                size: "small",
                required,
                error: !!error,
                helperText: error,
                onBlur: field.onBlur,
                sx: pillFieldSx,
              },
            }}
          />
        )}
      />
    </LocalizationProvider>
  )
}
